import React, { useEffect, useState, useRef } from 'react';
import * as d3 from 'd3';
import _ from 'underscore';

import HeatmapRadial from './HeatmapRadial';
import d3EzBase from './d3EzBase';

import Legend from "../Legend/Legend";

import { saveSvgAsPng } from 'save-svg-as-png';

export default function Heatmap(props) {

    const [ angleObject, setAngleObject ] = useState({
        'key': null,
        'order': null,
        'angles': [],
    })

    const [ dataObject, setDataObject ] = useState({});

    const { metric, direction, centerPoint, sparseAngle, limitedAngle, 
        sparsity, background, samplingStrategy, architecture, sortingObject, setSortingObject,
        showLabels } = props;

    const [ isLoaded, setLoaded ] = useState(false);

    const [ hasError, setError ] = useState(null);

    const domRef = useRef(null);
    const legendRef = useRef(null);

    const canvasPredRef = useRef();
    const canvasOrgRef = useRef();
    const canvasDiffRef = useRef();

    useEffect(() => {
        let newSortingObject = {
            'samplingStrategy': samplingStrategy,
            'sparsity': sparsity,
            'background': background,
            'limitedAngle': limitedAngle,
            'sparseAngle': sparseAngle,
            'centerPoint': centerPoint,
            'architecture': architecture,
            'metric': metric,
            'direction': direction,
            'x_axis': 'X',
            'y_axis': 'Z'
        }

        const newFileObj = getFetchString(newSortingObject, true)
        const newFileStr = newFileObj['fileStr'];
        const oldFileStr = dataObject['fileStr'];

        console.log(newFileStr);
        console.log(oldFileStr);

        if (_.isEmpty(dataObject) && domRef.current && !_.isEqual(newSortingObject, sortingObject)) {
            setSortingObject(newSortingObject);
            setLoaded(false);
            getDataset(newFileObj).then(
                (dataObj) => {Object.keys(dataObj).length > 0 && drawChart(dataObj, newSortingObject)});
        } else if (newFileStr !== oldFileStr || (!domRef.current && !hasError)) {
            setSortingObject(newSortingObject);
            setLoaded(true);
            getDataset(newFileObj).then(
                (dataObj) => {
                    setLoaded(true);
                    if (!dataObj['error']) {
                        if (!dataObject['error']) {
                            d3.select(domRef.current).selectAll("*").remove();
                            d3.select(legendRef.current).selectAll("*").remove();
                        }
                        // setLoaded(true);
                        drawChart(dataObj, newSortingObject);
                    }
                });
        }

        const predCanvas = canvasPredRef.current;
        const orgCanvas = canvasOrgRef.current;
        const diffCanvas = canvasDiffRef.current;
        Object.keys(dataObject).length > 0 && angleObject['key'] !== null && drawImages(predCanvas, orgCanvas, diffCanvas);
    }, [dataObject, metric, centerPoint, direction, sparseAngle, limitedAngle, angleObject, background, sparsity, samplingStrategy, architecture, hasError])

    function getFetchString(sortingObject, fetchMetrics) {
        let fileStr = process.env.REACT_APP_GOOGLE_URL;
        let folderName = '';
        let subFolderName = `${sortingObject['limitedAngle']}-${sortingObject['sparseAngle']}-${sortingObject['centerPoint']}`;

        const samplingStrategy = sortingObject['samplingStrategy'];
        const background = sortingObject['background'];
        const sparsity = sortingObject['sparsity'];
        const architecture = sortingObject['architecture'];

        let objectStr = '';

        if ( samplingStrategy === 'frangi' && architecture === '4x128' ) {
            if (!background && sparsity === 'ct') {
                folderName += 'limited-sparse-ct';
            } else if (background && sparsity === 'ct') {
                folderName += 'background-ct'
            } else if ( sparsity === 'lca' ) {
                folderName += 'sparsity-lca'
            }
        } else {
            if (architecture !== '4x128') {
                folderName += `architecture-${architecture}-${sparsity}`
            } else if (!background) {
                folderName += `sparsity-${samplingStrategy}-${sparsity}`
            } else {
                folderName += `background-${samplingStrategy}-${sparsity}`
            }
        }

        fileStr += `/${folderName}`;
        fileStr += `/${subFolderName}/`;
        if (fetchMetrics) {
            objectStr = `${sortingObject['metric']}-${sortingObject['direction']}-${sortingObject['x_axis']}-${sortingObject['y_axis']}.json`;
            fileStr += `${sortingObject['metric']}-${sortingObject['direction']}-${sortingObject['x_axis']}-${sortingObject['y_axis']}.json`;
        } else {
            objectStr = `${angleObject.angles[0]}.0${angleObject.angles[1]}.0.json`;
            fileStr += `${angleObject.angles[0]}.0${angleObject.angles[1]}.0.json`;
        }

        const fileObject = {
            folderName: folderName,
            subFolderName: subFolderName,
            objectStr: objectStr,
            fileStr: fileStr,
        }

        return fileObject;
    }

    function drawImages(predCanvas, orgCanvas, diffCanvas) {
        let orgWidth = 100;
        let orgHeight = 100;
        if (sortingObject['sparsity'] === 'lca') {
            orgWidth = 150;
            orgHeight = 162;
        }

        const canvasWidth = predCanvas.width;
        const canvasHeight = predCanvas.height;

        const predCtx = predCanvas.getContext('2d');
        const orgCtx = orgCanvas.getContext('2d');
        const diffCtx = diffCanvas.getContext('2d');



        // get image from respective file
        const fileObj = getFetchString(sortingObject, false);
        let url = process.env.REACT_APP_GOOGLE_URL;

        if (url.includes('google')) {
            const addedStr = `?folderName=${fileObj['folderName']}&subFolderName=${fileObj['subFolderName']}&objectStr=${fileObj['objectStr']}&metric=false`;
            url += addedStr;
        } else {
            url += `/${fileObj['folderName']}/${fileObj['subFolderName']}/${fileObj['objectStr']}`
        }
        
        fetch(url, { redirect: 'follow', method: 'GET', headers: { "Content-Type": "text/plain;charset=utf-8" } })
            .then(res => res.json())
            .then((json_imgs) => {

                const predImage = json_imgs['pred']// [phiIndex];
                const orgImage = json_imgs['org']//[phiIndex];
                const diffImage = json_imgs['diff']//[phiIndex];

                // convert to rgba
                let j = 0;
                let stepSize = 4;

                let predDataImage = predCtx.createImageData(orgWidth, orgHeight);
                let orgDataImage = orgCtx.createImageData(orgWidth, orgHeight);
                let diffDataImage = diffCtx.createImageData(orgWidth, orgHeight);

                for (let i = 0; i < predImage.length; i++) {
                    for (let count = 0; count < stepSize - 1; count++) {
                        predDataImage.data[j+count] = 255 * predImage[i];
                        orgDataImage.data[j+count] =  255 * orgImage[i];
                        diffDataImage.data[j+count] = 255 * diffImage[i];
                    }

                    predDataImage.data[j+3] = 255;
                    orgDataImage.data[j+3] =  255;
                    diffDataImage.data[j+3] = 255;

                    j += stepSize;
                }

                createImageBitmap(predDataImage).then(renderer => {
                    predCtx.drawImage(renderer, 0,0, canvasWidth, canvasHeight)
                })
                createImageBitmap(orgDataImage).then(renderer => 
                    orgCtx.drawImage(renderer, 0,0, canvasWidth, canvasHeight)
                )
                createImageBitmap(diffDataImage).then(renderer => 
                    diffCtx.drawImage(renderer, 0,0, canvasWidth, canvasHeight)
                )
            },
            (error) => {
                setLoaded(true);
                setError(error);
            })
    }

    function drawChart(dataObject, sortingObject) {
        let thresholds = [10, 50];
        if (metric === 'SSIM') {
            thresholds = [0.92, 1]
            if (sortingObject['sparsity'] === 'lca') {
                thresholds = [0.7, 1]
            } else if (sortingObject['background']) {
                thresholds = [0.3, 1]
            }
        } else if (metric === 'DICE 2D') {
            thresholds = [0.8, 1]
        } else if (metric === 'LPIPS' || metric === 'DISTS') {
            thresholds = [0.8, 1]
            if (sortingObject['sparsity'] === 'lca') {
                thresholds = [0.7, 1]
            } else if (sortingObject['background']) {
                thresholds = [0.3, 1]
            }
        }

        var size = 800;

        const ownColorScale = d3.scaleSequential(d3.interpolateCividis)
				.domain(thresholds);

        Legend(ownColorScale, legendRef, {
            title: `${sortingObject.metric}`,
            width: size,
        })

        let chart = null;
        if ( showLabels ) {
            chart = HeatmapRadial()
            .colorScale(ownColorScale)
            .sectorLabels(dataObject['sectorLabels'])
            .ringLabels(dataObject['ringLabels'])
            .thresholds(thresholds)
            .innerRadius(5);
        } else {
            chart = HeatmapRadial()
            .colorScale(ownColorScale)
            .thresholds(thresholds)
            .innerRadius(5);
        }

        // Create chart base
        var myChart = d3EzBase()
            .width(size)
            .height(size)
            .chart(chart)
            // .title(title)
            .on("customValueMouseOver", (d) => {
                    setAngleObject(d.data);
                }
            )

        d3.select(domRef.current)
            .datum(dataObject['data'])
            .call(myChart)
            .on('click', function() {
                const chartSvg = d3.select(domRef.current).select('svg')._groups[0][0];
                const fileStr = `${sortingObject['limitedAngle']}-${sortingObject['sparseAngle']}.png`;
                saveSvgAsPng(chartSvg, fileStr);
            });
        
    }
    

    async function getDataset(fileObj) {
        let dataObj = {};

        let url = process.env.REACT_APP_GOOGLE_URL.toString();

        if (url.includes('google')) {
            const addedStr = `?folderName=${fileObj['folderName']}&subFolderName=${fileObj['subFolderName']}&objectStr=${fileObj['objectStr']}&metric=true`;
            url += addedStr;
        } else {
            url += `/${fileObj['folderName']}/${fileObj['subFolderName']}/${fileObj['objectStr']}`
        }

        await fetch(url, { redirect: 'follow', method: 'GET', headers: { "Content-Type": "text/plain;charset=utf-8" } })
            .then(res => res.json())
            .then(
                (json) => {
                    setError(null);

                    const thetas = json['theta'];
                    const rads = json['rad']
                    let vals = json['vals'];
                    const angles = json['angles'];

                    if (metric === 'LPIPS' || metric === 'DISTS') {
                        console.log(vals);
                        vals = vals.map(v => 1 - v);
                        console.log(vals)
                    }
    
                    let data = [];
                    const uniqueThetas = [...new Set(thetas)];
                    const uniqPosThetas = uniqueThetas.map(v => v < 0 ? Math.PI - v : v );
                    
                    let orderDict = {};
                    uniqueThetas.forEach((key, i) => orderDict[key] = uniqPosThetas[i]);
    
                    const firstQuarterThetas = uniqueThetas.filter(v => v <= Math.PI / 2 && v >= 0);
    
                    let ringLabels = {}
                    let sectorLabels = {}
    
                    for (let i = 0; i < thetas.length; i++) {
                        let theta = thetas[i];
                        let rad = rads[i];
                        let angle = angles[i]
    
                        if (rad === 0) {
                            // fix incorrect theta values
                            const filtered = Object.fromEntries(Object.entries(sectorLabels).filter(([k,v]) => v === angle[1]));
                            theta = Object.keys(filtered)[0] ? Number(Object.keys(filtered)[0]) : theta;
                        }
    
                        let order = orderDict[theta];
                        if (theta >= 0 && firstQuarterThetas.includes(theta)) {
                            order = order + 2*Math.PI
                            // order = firstQuarterThetas.indexOf(theta) + uniqueThetas.length; 
                        }
    
                        let newObject = { key: theta, order: order, value: vals[i], angles: angle };
                        if (!data.map(a=>a.key).includes(rad)) {
                            // let keyObject = { key: thetas[i], values: [newObject] }
                            let keyObject = { key: rad, values: [newObject] }
                            data.push(keyObject);
    
                            ringLabels[rad] = angle[0];
                        } else {
                            let keyObject = data.filter(a => a.key === rad)[0];
    
                            let keyIndex = data.findIndex(a => a.key === rad);
                            
                            let values = keyObject.values;
    
                            if (!values.map(a=>a.key).includes(theta)) {
                                values.push(newObject);
                                // reorder values such that 0° is on x axis
                                values = values.sort((a,b) => a.order < b.order ? 1 : -1);
                                keyObject[keyIndex] = values;
    
                                sectorLabels[theta] = angle[1];
                            }
    
                            ringLabels[rad] = angle[0] < ringLabels[rad] && angle[0] >= 0 ? angle[0] : ringLabels[rad];
                            //Math.min(angle[0], ringLabels[rad]);
                        }
                    }
    
                    if (data[0].values.length !== data[data.length - 1].values.length) {
                        let newDataValues = [];
    
                        const realValues = data[0].values;
                        const oldValues = data[data.length - 1].values;
    
                        for (var keyVal in realValues) {
                            const phiAngle = realValues[keyVal]['angles'][1];
                            const newKey = realValues[keyVal]['key'];
                            const newOrder = realValues[keyVal]['order'];
                            // check if key already saved
                            const currentValObject = oldValues.filter(v => v.key === newKey);
    
                            if (currentValObject.length === 1) {
                                newDataValues.push(currentValObject[0])
                            } else {
    
                                let newObjects = oldValues.filter(v => v.angles[1] === phiAngle);
                                if (newObjects.length === 1) {
                                    let newObject = newObjects[0];
    
                                    newObject['key'] = newKey;
                                    newObject['order'] = newOrder;
    
                                    newDataValues.push(newObject)
                                }
                            }
                        }
                        data[data.length - 1].values = newDataValues;
                    }
                    
                    dataObj = { 'fileStr': fileObj['fileStr'], 'data': data, 'ringLabels': ringLabels, 'sectorLabels': sectorLabels };

                    Promise.resolve()
                        .then(() => { setDataObject(dataObj) })
                        .then(() => { setLoaded(true) })
                        .then(() => { return dataObj })
                },
                (error) => {
                    dataObj['fileStr'] = fileObj['fileStr'];
                    dataObj['error'] = true;

                    setDataObject(dataObj);
                    setTimeout(setLoaded(true));
                    setError(error);
                }
            )
        return dataObj;
    }

    return (
        <div style={{ display: 'flex', justifyContent: 'flex-start', margin: '1vw' }}>
            <div>
                <div style={{ display: 'flex', minWidth: '700px', width: '100px', flexWrap: 'wrap', gap: '1vw', margin: '0 1vw' }}>
                    <div
                        style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minWidth: '300px'}}>
                        {typeof angleObject.angles[0] !== 'undefined' && <span style={{ fontSize: '20pt', margin: '0.5vw 0' }}>{angleObject.angles[0] + '\u00B0 \u03B8'}</span>}
                        {typeof angleObject.angles[1] !== 'undefined' && <span style={{ fontSize: '20pt', margin: '0.5vw 0' }}>{angleObject.angles[1] + '\u00B0 \u03C6'}</span>}
                    </div>
                    {[{'Difference': canvasDiffRef}, {'Prediction': canvasPredRef}, {'Ground truth': canvasOrgRef}].map((canvasDict, i) => {
                        let canvasKey = Object.keys(canvasDict)[0];
                        return (
                            <div key={canvasKey} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '20pt'}}>{canvasKey}</span>
                                <canvas 
                                    ref={canvasDict[canvasKey]} 
                                    width='300px' 
                                    height='300px' 
                                    style={{ margin: '0.5vw 0'}}
                                />
                            </div>
                        )
                    })}
                    
                </div>
                
            </div>
            { hasError ? 
                <span>Could not load heatmap</span> : 
                !isLoaded ? 
                    <span>Loading...</span> :
                    <div>
                        <div ref={domRef} /> 
                        <svg ref={legendRef}
                            width='1000'
                        />
                    </div>
            }
        </div>
    )
}